import React, {useEffect, useState} from 'react';
import {Button, Col, Input, message, Row, Table} from 'antd';
import {PlusOutlined, EditOutlined} from '@ant-design/icons';
import {RequestDto, RequestControllerApi, SystemDto} from "../api";
import EditRequestModal from "../components/EditRequestModal";
import {useKeycloakHeader} from "../config/Config";
import {useKeycloak} from "@react-keycloak/web";
import {hasRole, ROLES} from "../components/misc/RolesHelper";
import MissingAuthorizationPage from "./error/MissingAuthorizationPage";
import {getErrorMessage} from "../utils/Utils";

interface RequestOverviewProps {
    system: SystemDto;
}

const RequestOverview = ({system}) => {
    const {keycloak} = useKeycloak();
    const [requestDto, setRequestDto] = useState<RequestDto[]>();
    const [searchText, setSearchText] = useState<string>(''); // Der Suchbegriff

    const [editModalVisible, setEditModalVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<RequestDto | null>(null);

    const headers = useKeycloakHeader();
    const requestControllerApi = new RequestControllerApi();

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Request JSON',
            dataIndex: 'requestJson',
            key: 'requestJson',
        },
        {
            title: 'Base URL',
            dataIndex: 'baseUrl',
            key: 'baseUrl',
        },
        {
            title: 'Stage',
            dataIndex: 'stage',
            key: 'stage',
        },
        {
            title: 'Request Method',
            dataIndex: 'requestMethod',
            key: 'requestMethod',
        },
        {
            title: 'Path Variables',
            dataIndex: 'pathVariables',
            key: 'pathVariables',
            render: (pathVariables) => (
                // Anzeige der Path Variables anpassen, z.B., in einer Liste oder durch Trennzeichen.
                pathVariables.join(', ')
            ),
        },
        {
            title: 'Edit',
            key: 'edit',
            render: (text, record) => (
                <span>
          <a onClick={() => handleEditClick(record)}>
            <EditOutlined style={{fontSize: '16px', color: '#1890ff'}}/>
          </a>
        </span>
            ),
        }
    ];

    useEffect(() => {
        fetchData();
    }, [system]);

    const fetchData = () => {
        requestControllerApi.getAllRequestsBySystem(system.name, headers)
            .then((axiosResponse) => {
                const axiosData: Array<RequestDto> = axiosResponse.data;
                setRequestDto(axiosData);
            })
            .catch((error) => {
                let msg = "Fehler beim Abrufen der Daten: ";
                message.error(msg + getErrorMessage(error));
            });
    };

    // Funktion zum Filtern der Tabelle basierend auf dem Suchbegriff
    const filteredData = requestDto?.filter((test) =>
        test.name?.toLowerCase().includes(searchText.toLowerCase())
    );

    const handleEditClick = (record: RequestDto | null) => {
        setSelectedRecord(record);
        setEditModalVisible(true);
    };

    const handleEditCancel = () => {
        setSelectedRecord(null);
        setEditModalVisible(false);
    };

    const handleEditSave = (updatedData: RequestDto) => {
        updatedData.system = system;
        if (!updatedData.id) {
            createNewRequest(updatedData);
        } else {
            updateExistingRequest(updatedData);
        }
        setEditModalVisible(false);
    };

    const createNewRequest = (request: RequestDto) => {
        requestControllerApi.createRequest(request, headers).then(response => {
            message.success('Request erstellt!');
            fetchData();
        }).catch((error) => {
            let msg = "Request konnte nicht erstellt werden! ";
            message.error(msg + getErrorMessage(error));
        });
    }

    const updateExistingRequest = (request: RequestDto) => {
        requestControllerApi.updateRequest(request, headers).then(response => {
            message.success('Request aktualisiert!');
            fetchData();
        }).catch((error) => {
            let msg = "Request konnte nicht aktualisiert werden! ";
            message.error(msg + getErrorMessage(error));
        });
    }

    let roles = [
        ROLES.GLOW_ADMIN,
        ROLES.GLOW_TEST_DEVELOPER,
        ROLES.GLOW_TEST_DESIGNER,
        ROLES.GLOW_TEST_MANAGER
    ];
    const hasRoleForPage = hasRole(roles, keycloak);
    if (!hasRoleForPage) {
        return (<MissingAuthorizationPage roles={roles}/>);
    }
    return (
        <div>
            <h1>Requests</h1>
            <Row>
                <Col flex={"70px"}>
                    <Button disabled={true} type="primary" onClick={() => handleEditClick(null)}><PlusOutlined/></Button>
                </Col>
                <Col flex={"auto"}>
                    <Input
                        placeholder="Nach Namen suchen"
                        onChange={(e) => setSearchText(e.target.value)}
                        value={searchText}
                    />
                </Col>
            </Row>

            <Table dataSource={filteredData} columns={columns} locale={{ emptyText: 'Keine Daten vorhanden' }}/>
            <EditRequestModal
                visible={editModalVisible}
                onCancel={handleEditCancel}
                onSave={handleEditSave}
                request={selectedRecord}
            />
        </div>
    );
};

export default RequestOverview;